import { Line } from '@react-three/drei';
import React, { useMemo } from 'react';
import * as THREE from 'three';

import { MeasureKind, UnitsByMeasure } from 'convert';
import { useIntl } from 'react-intl';
import { WallLengthIndicatorProps } from '../../../@types/Components';
import { EUnitType } from '../../../@types/Enums';
import { useLanguage } from '../../../provider/LanguageProvider';
import { useMultiSelectToggleContext } from '../../../provider/MultiSelectToggleProvider';
import { useUnitContext } from '../../../provider/UnitProvider';
import {
  DEFAULT_INDICATOR_COLOR,
  DEFAULT_INDICATOR_FONT_SIZE,
  DEFAULT_INDICATOR_LINE_WIDTH,
  DEFAULT_WALL_OUTER_OFFSET,
  WALL_TEXT_OFFSET,
} from '../../../utils/constants';
import { convertTo, getLocalizedUnitLabel } from '../../../utils/conversionUtils';
import { localizeNumber } from '../../../utils/numberUtils';
import RENDER_ORDERS from '../../../utils/renderOrder';
import RoundedBoxWithText from '../RoundedBoxWithText';

type OuterWallLengthIndicatorProps = WallLengthIndicatorProps & {
  forceShow?: boolean;
};

const OuterWallLengthIndicator = ({
  startPoint,
  endPoint,
  outerStartPoint,
  outerEndPoint,
  wallSideBLength,
  color = DEFAULT_INDICATOR_COLOR,
  lineWidth = DEFAULT_INDICATOR_LINE_WIDTH,
  isWallSelected = false,
  forceShow = false,
}: OuterWallLengthIndicatorProps): React.JSX.Element => {
  const { showWallOuterLength } = useMultiSelectToggleContext();
  const { unitType } = useUnitContext();
  const { locale } = useLanguage();
  const intl = useIntl();

  const mLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'meter'), [intl, locale]);
  const ftLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'foot'), [intl, locale]);

  const toVector3 = (point: number[] | THREE.Vector3): THREE.Vector3 => {
    if ((point as THREE.Vector3).isVector3) {
      return point as THREE.Vector3;
    }

    return new THREE.Vector3((point as number[])[0], (point as number[])[1], 0);
  };

  const pointA = useMemo(() => toVector3(startPoint), [startPoint]);
  const pointB = useMemo(() => toVector3(endPoint), [endPoint]);
  const vectorAB = useMemo(() => new THREE.Vector3().subVectors(pointB, pointA), [pointA, pointB]);
  const perpendicular = useMemo(() => new THREE.Vector3(vectorAB.y, -vectorAB.x).normalize(), [vectorAB]);

  const outerOffset = useMemo(() => perpendicular.clone().multiplyScalar(-DEFAULT_WALL_OUTER_OFFSET), [perpendicular]);

  const pointC = outerStartPoint ? toVector3(outerStartPoint) : null;
  const pointD = outerEndPoint ? toVector3(outerEndPoint) : null;
  const pointOuterC = pointC ? pointC.clone().add(outerOffset) : null;
  const pointOuterD = pointD ? pointD.clone().add(outerOffset) : null;

  const midPointOuter = useMemo(() => {
    if (pointOuterC && pointOuterD) {
      const textPerpendOuter = perpendicular.clone().multiplyScalar(-WALL_TEXT_OFFSET);
      const aboveOuterC = pointOuterC.clone().add(textPerpendOuter);
      const aboveOuterD = pointOuterD.clone().add(textPerpendOuter);

      return new THREE.Vector3((aboveOuterC.x + aboveOuterD.x) / 2, (aboveOuterC.y + aboveOuterD.y) / 2, 1);
    }

    return null;
  }, [pointOuterC, pointOuterD, perpendicular]);

  const sideBlengthText = useMemo(() => {
    if (!wallSideBLength) return '';

    const value =
      unitType === EUnitType.METRIC
        ? localizeNumber(wallSideBLength.toFixed(2), locale) + mLocalizedLabel
        : localizeNumber(convertTo(wallSideBLength, 'meters' as UnitsByMeasure<MeasureKind.Length>, 'feet' as UnitsByMeasure<MeasureKind.Length>).toFixed(2), locale) +
          ftLocalizedLabel;

    return isWallSelected ? `L2: ${value}` : value;
  }, [wallSideBLength, unitType, locale, isWallSelected, mLocalizedLabel, ftLocalizedLabel]);

  const boxSize = useMemo(() => {
    if (isWallSelected) {
      return [3.9, 1.2, 0];
    }

    return [2.9, 1.2, 0];
  }, [isWallSelected]);

  const shouldShow = forceShow || showWallOuterLength;

  return (
    <group name={'group-wallLengthIndicator'} renderOrder={RENDER_ORDERS.lengthIndicator}>
      {wallSideBLength && pointOuterC && pointOuterD && shouldShow && (
        <>
          <Line name={'outerWallLengthIndicator-line'} points={[pointOuterC, pointOuterD]} color={color} lineWidth={lineWidth} transparent={true} />
          {pointC !== null && <Line name={'outerWallLengthIndicator-line'} points={[pointC, pointOuterC]} color={color} lineWidth={lineWidth} transparent={true} />}
          {pointD !== null && <Line name={'outerWallLengthIndicator-line'} points={[pointD, pointOuterD]} color={color} lineWidth={lineWidth} transparent={true} />}
          <RoundedBoxWithText position={midPointOuter!} boxSize={boxSize} fontSize={DEFAULT_INDICATOR_FONT_SIZE} textColor="black" text={sideBlengthText} />
        </>
      )}
    </group>
  );
};

export default OuterWallLengthIndicator;